/* istanbul ignore file */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Category,
  ListCategoriesRequest,
  CreateCategoriesRequest,
  GetCategoriesRequest,
  UpdateCategoriesRequest,
} from '../../types/helios/Category';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import { categoryService } from 'src/data/api/helios-service/categoryService';
import { handleError } from 'src/utils/generalUtils';
export interface CategoryState {
  category: Category[];
  fetchCategoriesApiStatus: ApiRequestStatus;
  createCategoriesApiStatus: ApiRequestStatus;
  getCategoriesApiStatus: ApiRequestStatus;
  updateCategoriesApiStatus: ApiRequestStatus;
}

const initialState: CategoryState = {
  category: [],
  fetchCategoriesApiStatus: initApiStatus,
  createCategoriesApiStatus: initApiStatus,
  getCategoriesApiStatus: initApiStatus,
  updateCategoriesApiStatus: initApiStatus,
};

export const fetchCategoriesApiThunk = createAsyncThunk(
  'category/fetchCategories',
  async (data: ListCategoriesRequest, { rejectWithValue }) => {
    try {
      const response = await categoryService.getAll(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);
export const getCategoriesApiThunk = createAsyncThunk(
  'category/getCategories',
  async (data: GetCategoriesRequest, { rejectWithValue }) => {
    try {
      const response = await categoryService.get(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);
export const createCategoriesApiThunk = createAsyncThunk(
  'category/addCategories',
  async (data: CreateCategoriesRequest, { rejectWithValue }) => {
    try {
      const response = await categoryService.create(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);
export const updateCategoriesApiThunk = createAsyncThunk(
  'category/updateCategories',
  async ({ data }: { data: UpdateCategoriesRequest }, { rejectWithValue }) => {
    try {
      const response = await categoryService.update(data);
      return {
        category: response?.Category,
        message: 'Category updated successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    resetAllApiStatus(state) {
      state.fetchCategoriesApiStatus = initApiStatus;
      state.updateCategoriesApiStatus = initApiStatus;
      state.createCategoriesApiStatus = initApiStatus;
      state.getCategoriesApiStatus = initApiStatus;
    },
    addCategoriesToState: (state, action) => {
      state.category.push(action.payload);
    },
    updateCategoriesInState: (state, action) => {
      const index = state.category.findIndex(
        (cat) =>
          cat.categoryName === action.payload.category.categoryName &&
          cat.businessUnit === action.payload.category.businessUnit &&
          cat.divisionName === action.payload.category.divisionName,
      );
      if (index !== -1) {
        const createdDate = state.category[index].createdAt;
        const createdBy = state.category[index].createdBy;
        const data = { ...state.category[index], ...action.payload.category };
        if (!action.payload.category.createdAt || !action.payload.category.createdBy) {
          data.createdAt = createdDate;
          data.createdBy = createdBy;
        }
        state.category[index] = data;
      }
    },

    clearAPIStatusMessageFromState(state) {
      state.fetchCategoriesApiStatus = initApiStatus;
      state.updateCategoriesApiStatus = initApiStatus;
      state.createCategoriesApiStatus = initApiStatus;
      state.getCategoriesApiStatus = initApiStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategoriesApiThunk.pending, (state) => {
        state.fetchCategoriesApiStatus.isLoading = true;
        state.fetchCategoriesApiStatus.errorMessage = null;
      })
      .addCase(fetchCategoriesApiThunk.fulfilled, (state, action) => {
        state.fetchCategoriesApiStatus.isLoading = false;
        state.fetchCategoriesApiStatus.isSuccess = true;
        state.category = action.payload.Categories;
        state.fetchCategoriesApiStatus.nextToken = action.payload.nextToken;
      })
      .addCase(fetchCategoriesApiThunk.rejected, (state, action) => {
        state.fetchCategoriesApiStatus.isLoading = false;
        state.fetchCategoriesApiStatus.isSuccess = false;
        state.fetchCategoriesApiStatus.errorMessage = action.payload as string;
      });
    //Add Category
    builder
      .addCase(createCategoriesApiThunk.pending, (state) => {
        state.createCategoriesApiStatus.isLoading = true;
        state.createCategoriesApiStatus.errorMessage = null;
        state.createCategoriesApiStatus.successMessage = null;
      })
      .addCase(createCategoriesApiThunk.fulfilled, (state, action) => {
        state.createCategoriesApiStatus.isLoading = false;
        state.createCategoriesApiStatus.isSuccess = true;
        state.createCategoriesApiStatus.successMessage = action.payload.message;
      })
      .addCase(createCategoriesApiThunk.rejected, (state, action) => {
        state.createCategoriesApiStatus.isLoading = false;
        state.createCategoriesApiStatus.isSuccess = false;
        state.createCategoriesApiStatus.errorMessage = action.payload as string;
        state.createCategoriesApiStatus.successMessage = null;
      });
    builder
      .addCase(getCategoriesApiThunk.pending, (state) => {
        state.getCategoriesApiStatus.isLoading = true;
        state.getCategoriesApiStatus.errorMessage = null;
        state.getCategoriesApiStatus.isSuccess = false;
      })
      .addCase(getCategoriesApiThunk.fulfilled, (state, action) => {
        state.getCategoriesApiStatus.isLoading = false;
        state.getCategoriesApiStatus.isSuccess = true;
      })
      .addCase(getCategoriesApiThunk.rejected, (state, action) => {
        state.getCategoriesApiStatus.isLoading = false;
        state.getCategoriesApiStatus.isSuccess = false;
        state.getCategoriesApiStatus.errorMessage = action.payload as string;
      });
    builder
      .addCase(updateCategoriesApiThunk.pending, (state, action) => {
        state.updateCategoriesApiStatus.isLoading = true;
        state.updateCategoriesApiStatus.errorMessage = null;
        state.updateCategoriesApiStatus.successMessage = null;
      })
      .addCase(updateCategoriesApiThunk.fulfilled, (state, action) => {
        state.updateCategoriesApiStatus.isLoading = false;
        state.updateCategoriesApiStatus.isSuccess = true;
        state.updateCategoriesApiStatus.successMessage = action.payload.message;
        categoriesSlice.caseReducers.updateCategoriesInState(state, {
          type: 'updateCategoriesInState',
          payload: action.payload,
        });
      })
      .addCase(updateCategoriesApiThunk.rejected, (state, action) => {
        state.updateCategoriesApiStatus.isLoading = false;
        state.updateCategoriesApiStatus.isSuccess = false;
        state.updateCategoriesApiStatus.errorMessage = action.payload as string;
        state.updateCategoriesApiStatus.successMessage = null;
      });
  },
});

const categoriesReducer = (state: CategoryState = initialState, action: any) => {
  return categoriesSlice.reducer(state, action);
};
export const {
  updateCategoriesInState,
  resetAllApiStatus,
  clearAPIStatusMessageFromState,
  addCategoriesToState,
} = categoriesSlice.actions;
export default categoriesReducer;
